import { useEffect, useState } from 'react';

const VIEWPORT_KEYBOARD_THRESHOLD_PX = 80;
const VIEWPORT_RECOVERY_DELAY_MS = 140;

function isTextInputLike(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable;
}

export function useVisualViewport() {
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const visualViewport = window.visualViewport;
    let baselineHeight = Math.max(window.innerHeight, visualViewport?.height ?? 0);
    let keyboardFocused = false;
    let rafId: number | null = null;
    let timeoutId: number | null = null;

    const clearScheduledWork = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const readViewportHeight = (resetBaseline = false) => {
      const innerHeight = window.innerHeight;
      const visualHeight = visualViewport?.height ?? innerHeight;

      if (resetBaseline) {
        baselineHeight = Math.max(innerHeight, visualHeight);
      } else {
        baselineHeight = Math.max(baselineHeight, innerHeight, visualHeight);
      }

      const keyboardLikelyOpen =
        keyboardFocused && visualHeight < baselineHeight - VIEWPORT_KEYBOARD_THRESHOLD_PX;

      return Math.round(
        keyboardLikelyOpen ? visualHeight : Math.max(innerHeight, visualHeight, baselineHeight),
      );
    };

    const applyViewportHeight = (resetBaseline = false) => {
      const nextHeight = readViewportHeight(resetBaseline);
      setViewportHeight((previousHeight) =>
        previousHeight === nextHeight ? previousHeight : nextHeight,
      );
    };

    const scheduleViewportSample = (options?: {
      resetBaseline?: boolean;
      includeDelayedPass?: boolean;
    }) => {
      clearScheduledWork();
      rafId = window.requestAnimationFrame(() => {
        applyViewportHeight(options?.resetBaseline);
        if (options?.includeDelayedPass) {
          timeoutId = window.setTimeout(() => {
            applyViewportHeight(options?.resetBaseline);
          }, VIEWPORT_RECOVERY_DELAY_MS);
        }
      });
    };

    const handleViewportEvent = () => scheduleViewportSample();
    const handleWindowResize = () =>
      scheduleViewportSample({ includeDelayedPass: !keyboardFocused });
    const handleOrientationChange = () =>
      scheduleViewportSample({ resetBaseline: true, includeDelayedPass: true });
    const handleFocusIn = (event: FocusEvent) => {
      keyboardFocused = isTextInputLike(event.target);
      scheduleViewportSample();
    };
    const handleFocusOut = () => {
      keyboardFocused = false;
      scheduleViewportSample({ includeDelayedPass: true });
    };

    applyViewportHeight(true);

    visualViewport?.addEventListener('resize', handleViewportEvent);
    visualViewport?.addEventListener('scroll', handleViewportEvent);
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);

    return () => {
      clearScheduledWork();
      visualViewport?.removeEventListener('resize', handleViewportEvent);
      visualViewport?.removeEventListener('scroll', handleViewportEvent);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return viewportHeight;
}
